import React, { type ElementType } from "react";
import {
  Box,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToast,
  Icon,
  IconButton,
} from "@chakra-ui/react";
import { FiShare2 } from "react-icons/fi";
import {
  FaEnvelope,
  FaFacebook,
  FaLink,
  FaLinkedin,
  FaWhatsapp,
} from "react-icons/fa";
import { IoShareSocialOutline } from "react-icons/io5";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
} from "react-share";
import { useTranslation } from "react-i18next";
import { buildReaderBookShareUrl, buildShareBookOgPageUrl } from "../constants";

export type ShareBookInfo = {
  title: string;
  coverUrl?: string | null;
};

type BookShareProps = {
  book: ShareBookInfo;
  bookId: string;
  chapterOneBased: number;
};

/** Row label + icon to mirror Chakra ghost `Button` with `leftIcon`. */
function ShareRowContent({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap={2}
      fontSize="md"
    >
      {icon}
      {label}
    </Box>
  );
}

/** Shared row chrome for `<button>` and react-share’s `<button>`. */
function ShareGhostRow({ children }: { children: React.ReactNode }) {
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const rowBase = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "md",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
    fontSize: "md",
    lineHeight: "shorter",
    color: "inherit",
    textAlign: "left",
    boxSizing: "border-box",
    textDecoration: "none",
    WebkitAppearance: "none",
  } as const;
  return (
    <Box
      w="100%"
      borderRadius="md"
      sx={{
        "& > button": rowBase,
        "& > button:hover:not(:disabled)": {
          background: hoverBg,
          textDecoration: "none",
        },
        "& > button:disabled": {
          opacity: 0.5,
          cursor: "not-allowed",
        },
      }}
    >
      {children}
    </Box>
  );
}

const BookShare: React.FC<BookShareProps> = ({
  book,
  bookId,
  chapterOneBased,
}) => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const readerShareUrl =
    typeof window !== "undefined"
      ? buildReaderBookShareUrl(bookId, chapterOneBased)
      : "";
  const ogShareUrl =
    typeof window !== "undefined"
      ? buildShareBookOgPageUrl(bookId, chapterOneBased)
      : "";
  const shareCaption = t("readBook.share.captionMessage", {
    title: book.title,
    url: readerShareUrl,
  });

  const notifyShareCopyError = () => {
    toast({
      title: t("readBook.share.copyFailedTitle"),
      description: t("readBook.share.copyFailedDescription"),
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  };

  const copyCaptionFallback = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(shareCaption);
        toast({
          title: t("readBook.share.copiedTitle"),
          description: t("readBook.share.copiedCaptionDescription"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
      } catch {
        notifyShareCopyError();
      }
    })();
  };

  const handleCopyShareUrl = () => {
    void (async () => {
      if (!ogShareUrl) {
        notifyShareCopyError();
        return;
      }
      try {
        await navigator.clipboard.writeText(ogShareUrl);
        toast({
          title: t("readBook.share.copiedTitle"),
          description: t("readBook.share.copiedLinkDescription"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
      } catch {
        notifyShareCopyError();
      }
    })();
  };

  const handleShareLink = () => {
    const sharePayload: ShareData = {
      title: book.title,
      text: shareCaption,
      url: ogShareUrl,
    };

    const canUseNativeShare =
      typeof navigator.share === "function" &&
      (typeof navigator.canShare !== "function" ||
        navigator.canShare(sharePayload));

    const fallback = () => {
      copyCaptionFallback();
    };

    if (canUseNativeShare) {
      void navigator
        .share(sharePayload)
        .then(() => {
          onClose();
        })
        .catch((err: unknown) => {
          const name =
            err && typeof err === "object" && "name" in err
              ? String((err as DOMException).name)
              : "";
          if (name === "AbortError") {
            return;
          }
          fallback();
        });
      return;
    }

    fallback();
  };

  const closeModalBeforeShare = () => {
    onClose();
  };

  return (
    <>
      <IconButton
        id="read-book-share-button"
        aria-label={t("readBook.share.open")}
        icon={<Icon as={FiShare2 as ElementType} boxSize={4} />}
        size="md"
        isRound
        variant="outline"
        onClick={onOpen}
      />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={colorMode === "light" ? "white" : "gray.800"} mx={4}>
          <ModalHeader>{t("readBook.share.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box alignSelf="center">
                {book.coverUrl ? (
                  <Image
                    src={book.coverUrl}
                    alt={t("readBook.share.coverAlt", {
                      title: book.title,
                    })}
                    borderRadius="md"
                    maxH="200px"
                    maxW="100%"
                    objectFit="contain"
                    boxShadow="md"
                  />
                ) : (
                  <Box
                    w="140px"
                    h="200px"
                    borderRadius="md"
                    bg={colorMode === "light" ? "gray.100" : "gray.700"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={3}
                    boxShadow="md"
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      textAlign="center"
                      noOfLines={4}
                      color={colorMode === "light" ? "gray.600" : "gray.300"}
                    >
                      {book.title}
                    </Text>
                  </Box>
                )}
              </Box>
              <VStack align="stretch" spacing={1}>
                <ShareGhostRow>
                  <button
                    type="button"
                    id="read-book-share-link"
                    onClick={handleCopyShareUrl}
                    disabled={!ogShareUrl}
                    aria-label={t("readBook.share.link")}
                  >
                    <ShareRowContent
                      icon={<Icon as={FaLink as ElementType} boxSize={5} />}
                      label={t("readBook.share.link")}
                    />
                  </button>
                </ShareGhostRow>

                <ShareGhostRow>
                  <EmailShareButton
                    id="read-book-share-email"
                    url={ogShareUrl}
                    subject={t("readBook.share.emailSubject", {
                      title: book.title,
                    })}
                    body={t("readBook.share.emailBody", { title: book.title })}
                    separator={"\n\n"}
                    disabled={!ogShareUrl}
                    resetButtonStyle={false}
                    beforeOnClick={closeModalBeforeShare}
                    aria-label={t("readBook.share.email")}
                  >
                    <ShareRowContent
                      icon={<Icon as={FaEnvelope as ElementType} boxSize={5} />}
                      label={t("readBook.share.email")}
                    />
                  </EmailShareButton>
                </ShareGhostRow>

                <ShareGhostRow>
                  <FacebookShareButton
                    id="read-book-share-facebook"
                    url={ogShareUrl}
                    disabled={!ogShareUrl}
                    resetButtonStyle={false}
                    beforeOnClick={closeModalBeforeShare}
                    aria-label={t("readBook.share.facebook")}
                  >
                    <ShareRowContent
                      icon={<Icon as={FaFacebook as ElementType} boxSize={5} />}
                      label={t("readBook.share.facebook")}
                    />
                  </FacebookShareButton>
                </ShareGhostRow>

                <ShareGhostRow>
                  <LinkedinShareButton
                    id="read-book-share-linkedin"
                    url={ogShareUrl}
                    title={book.title}
                    disabled={!ogShareUrl}
                    resetButtonStyle={false}
                    beforeOnClick={closeModalBeforeShare}
                    aria-label={t("readBook.share.linkedin")}
                  >
                    <ShareRowContent
                      icon={<Icon as={FaLinkedin as ElementType} boxSize={5} />}
                      label={t("readBook.share.linkedin")}
                    />
                  </LinkedinShareButton>
                </ShareGhostRow>

                <ShareGhostRow>
                  <WhatsappShareButton
                    id="read-book-share-whatsapp"
                    url={ogShareUrl}
                    title={book.title}
                    separator={"\n"}
                    disabled={!ogShareUrl}
                    resetButtonStyle={false}
                    beforeOnClick={closeModalBeforeShare}
                    aria-label={t("readBook.share.whatsapp")}
                  >
                    <ShareRowContent
                      icon={<Icon as={FaWhatsapp as ElementType} boxSize={5} />}
                      label={t("readBook.share.whatsapp")}
                    />
                  </WhatsappShareButton>
                </ShareGhostRow>

                <ShareGhostRow>
                  <button
                    type="button"
                    id="read-book-share-other"
                    onClick={handleShareLink}
                    aria-label={t("readBook.share.other")}
                  >
                    <ShareRowContent
                      icon={
                        <Icon
                          as={IoShareSocialOutline as ElementType}
                          boxSize={5}
                        />
                      }
                      label={t("readBook.share.other")}
                    />
                  </button>
                </ShareGhostRow>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BookShare;
